import { useEffect, useState } from "react";
import { Save, UserCog, Lock, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePejabat } from "@/context/PejabatContext";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SignaturePad } from "@/components/SignaturePad";

const FIELDS = [
  ["nama", "Nama lengkap & gelar Lurah", "GERSON RUMBARAR, SE, M.Si"],
  ["nip", "NIP", "19810120 201001 1 016"],
  ["pangkat", "Pangkat / Golongan", "PENATA TK. I"],
  ["jabatan", "Jabatan", "Kepala Kelurahan Inauga"],
];
const DISTRIK = [
  ["distrik_nama", "Nama Kepala Distrik Wania", "Kosongkan jika ditandatangani manual"],
  ["distrik_nip", "NIP Kepala Distrik", ""],
];

export default function Pengaturan() {
  const { isAdmin } = useAuth();
  const { pejabat, setPejabat } = usePejabat();
  const [f, setF] = useState(pejabat);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setF(pejabat);
  }, [pejabat]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/pejabat", f);
      setPejabat(data);
      toast.success("Data pejabat penandatangan diperbarui");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const field = ([k, label, ph]) => (
    <div key={k}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input value={f[k] || ""} placeholder={ph} disabled={!isAdmin} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} className="mt-1 bg-white" data-testid={`pejabat-${k}-input`} />
    </div>
  );

  return (
    <div data-testid="pengaturan-page">
      <PageHeader kicker="Pengaturan Sistem" title="Pejabat Penandatangan" />
      {!isAdmin && (
        <div className="mb-4 flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2.5" data-testid="pengaturan-view-only">
          <Lock className="h-4 w-4" /> Mode lihat — hanya Admin yang dapat mengubah data pejabat.
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_420px] gap-6 items-start">
        <form onSubmit={save} className="bg-white border rounded-2xl p-6 space-y-6" data-testid="pejabat-form">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-amber-700"><UserCog className="h-4 w-4" /> Kepala Kelurahan</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-3">{FIELDS.map(field)}</div>
          </div>
          <div className="border-t pt-5">
            <div className="text-xs font-bold tracking-widest uppercase text-amber-700">Kepala Distrik (opsional)</div>
            <p className="text-xs text-slate-500 mt-1">Dipakai pada surat yang memerlukan tanda tangan “Mengetahui Kepala Distrik Wania” (mis. SKTM, Sedang Membangun).</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-3">{DISTRIK.map(field)}</div>
          </div>
          <div className="border-t pt-5">
            <div className="text-xs font-bold tracking-widest uppercase text-amber-700">Tanda Tangan Elektronik Kepala Kelurahan</div>
            <p className="text-xs text-slate-500 mt-1 mb-3">Tanda tangan ini otomatis dibubuhkan pada setiap surat baru, Cetak Rekap, dan Rekap Surat.</p>
            <SignaturePad value={f.ttd} disabled={!isAdmin} onChange={(v) => setF((p) => ({ ...p, ttd: v }))} />
            <label className="flex items-center gap-3 mt-4 text-sm">
              <Switch checked={f.qr !== false} disabled={!isAdmin} onCheckedChange={(v) => setF((p) => ({ ...p, qr: v }))} data-testid="pejabat-qr-switch" />
              Tampilkan QR verifikasi keaslian surat
            </label>
          </div>
          {isAdmin && (
            <div className="flex justify-end">
              <Button type="submit" disabled={saving} className="bg-[#0b4f4a] hover:bg-[#072f2c]" data-testid="pejabat-save-button">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
              </Button>
            </div>
          )}
        </form>
        <div className="space-y-4">
          <div className="bg-white border rounded-2xl p-6" data-testid="pejabat-preview">
            <div className="text-xs font-bold tracking-widest uppercase text-slate-500 mb-4">Pratinjau Tanda Tangan</div>
            <div className="text-center text-[15px]" style={{ fontFamily: "'Times New Roman', serif" }}>
              <div>Mengetahui :</div>
              <div className="font-semibold">{f.jabatan || "…"}</div>
              {f.ttd ? <img src={f.ttd} alt="ttd" className="h-16 mx-auto object-contain" data-testid="pejabat-preview-ttd" /> : <div className="h-16" />}
              <div className="font-bold underline" data-testid="pejabat-preview-nama">{f.nama || "…"}</div>
              {f.pangkat && <div>{f.pangkat}</div>}
              <div>NIP. {f.nip || "…"}</div>
            </div>
          </div>
          <div className="rounded-2xl bg-[#072f2c] text-emerald-50 p-5 text-sm leading-relaxed flex gap-3">
            <BadgeCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <p>Perubahan berlaku untuk surat baru dan Cetak Rekap. Surat yang sudah tersimpan tetap memakai pejabat saat surat diterbitkan, sehingga arsip tetap sah.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
